import ItemCountButton from "@/components/checkout/item-count-button";
import FluidImage from "@/components/images/fluid-image";
import { FLUID_CDN_URL } from "@/lib/constants";
import { cn, formatCurrency, calculateTotalSavings } from "@/lib/utils";
import CartItemError from "@/components/checkout/cart-item-error";
import {
  SubscriptionPlan,
  VariantOption,
  CartItem,
} from "@/networking/carts.api";
import SubscriptionPlansDropdown from "@/components/checkout/subscription-plans-dropdown";
import { useTranslation } from "@/hooks/useTranslation";
import { pointsLabel } from "@/lib/points-label";
import BundleItem from "@/components/checkout/bundle-item";
import EnrollmentItem from "@/components/checkout/enrollment-item";
import { Badge } from "../fluid-ui/badge";
import { useState } from "react";
import VariantSelectionDropdown from "./variant-selection-dropdown";
import { productLineTotals } from "./product-line-totals";

interface ProductProps {
  productTitle: string;
  variantOptions: VariantOption[];
  imageUrl: string;
  originalPrice: number;
  displayPrice: number;
  lineTotalInCurrency?: string | null;
  lineTotalAfterDiscountsInCurrency?: string | null;
  subscriptionDetails?: string;
  quantity: number;
  variantId?: number;
  cartToken: string;
  currencySymbol: string;
  currencyDecimalPlaces?: number;
  currencyCode?: string;
  isSubscribed?: boolean;
  updateSubscription: (
    subscriptionPlanId?: number,
    subscription?: boolean,
  ) => void;
  subscribeAndSave?: string | null;
  savingsLabel?: string | null;
  allowSubscription?: boolean;
  itemId: number;
  productId?: number;
  errors?: Array<Record<string, string[]>> | null;
  paymentMethodsIncludes: string | null;
  subscriptionPlans: SubscriptionPlan[] | null;
  selectedSubscriptionPlanId?: number;
  isLast?: boolean;
  isFree?: boolean;
  isBundle?: boolean;
  bundleItems?: CartItem["bundled_items"];
  isSubscriptionPending?: boolean;
  isQuantityPending?: boolean;
  enrollmentPackData?: {
    enrollment_pack_id: number;
    title: string;
    imageUrl?: string;
  } | null;
  enrollmentPackId?: number | null;
  displayPromotionsHeader?: boolean;
  creditablePoints?: number;
  rewardPointsEnabled?: boolean;
  rewardPointsLabelSingular?: string | null;
  rewardPointsLabelPlural?: string | null;
  // Per-unit commissionable/qualifying volume for this line. Rendered only
  // when showVolume is true (rep carts). Multiplied by quantity for display
  // so the per-line totals sum to the cart-level cv_total/qv_total.
  cv?: number;
  qv?: number;
  showVolume?: boolean;
}

const Product: React.FC<ProductProps> = ({
  productTitle,
  variantOptions,
  imageUrl,
  originalPrice,
  displayPrice,
  lineTotalInCurrency,
  lineTotalAfterDiscountsInCurrency,
  subscriptionDetails,
  quantity,
  variantId,
  cartToken,
  currencySymbol,
  currencyDecimalPlaces,
  currencyCode,
  isSubscribed,
  updateSubscription,
  subscribeAndSave,
  savingsLabel,
  allowSubscription,
  itemId,
  productId,
  errors,
  paymentMethodsIncludes,
  subscriptionPlans,
  selectedSubscriptionPlanId,
  isLast = false,
  isFree = false,
  isBundle = false,
  bundleItems = [],
  isSubscriptionPending = false,
  isQuantityPending = false,
  enrollmentPackData,
  enrollmentPackId,
  displayPromotionsHeader = false,
  creditablePoints,
  rewardPointsEnabled = false,
  rewardPointsLabelSingular,
  rewardPointsLabelPlural,
  cv,
  qv,
  showVolume = false,
}) => {
  const [showVariantSelection, setShowVariantSelection] = useState(false);
  const { t } = useTranslation();
  const pointsLabelConfig = {
    reward_points_label_singular: rewardPointsLabelSingular,
    reward_points_label_plural: rewardPointsLabelPlural,
  };
  const currencyFormatOptions = {
    symbol: currencySymbol,
    decimalPlaces: currencyDecimalPlaces,
    currencyCode,
  };
  const { originalLineTotal, currentLineTotal } = productLineTotals({
    originalPrice,
    displayPrice,
    quantity,
    subscribeAndSave,
    lineTotalInCurrency,
    lineTotalAfterDiscountsInCurrency,
    currencySymbol,
    currencyDecimalPlaces,
    currencyCode,
    isFree,
  });

  // Line-level volume = per-unit volume × quantity (QV/CV are stored per unit).
  // Summed across lines these equal the cart-level cv_total/qv_total.
  const cvTotal = (cv ?? 0) * quantity;
  const qvTotal = (qv ?? 0) * quantity;
  const displayVolume = showVolume && (cvTotal > 0 || qvTotal > 0);

  const isEnrollment = !!enrollmentPackId;

  const bundleItemsToDisplay =
    bundleItems && bundleItems.length > 0
      ? bundleItems.filter((bundleItem) => bundleItem.display_to_customer)
      : [];

  const displayTitle =
    isEnrollment &&
    enrollmentPackData &&
    enrollmentPackId === enrollmentPackData.enrollment_pack_id
      ? enrollmentPackData.title
      : productTitle;

  const matchingEnrollmentPack =
    isEnrollment &&
    enrollmentPackData &&
    enrollmentPackId === enrollmentPackData.enrollment_pack_id
      ? enrollmentPackData
      : null;

  return (
    <li
      className={cn("group relative flex flex-col py-6", {
        "border-b border-gray-200": !isLast,
      })}
    >
      {isFree && displayPromotionsHeader && (
        <div className="absolute top-[-12px] left-0 group-first:static group-first:mb-6 group-first:flex group-first:w-full group-first:items-center group-first:gap-3">
          <span className="bg-gray-50 pr-3 text-sm font-semibold whitespace-nowrap text-gray-900 group-first:bg-transparent group-first:pr-0">
            {t("promotions")}
          </span>
          <span className="hidden min-w-0 flex-1 border-t border-gray-200 group-first:block" />
        </div>
      )}
      <div className="flex items-start justify-between sm:items-center">
        <div className="flex min-w-0 flex-1 flex-row space-x-6">
          <FluidImage
            alt={displayTitle || t("product")}
            src={imageUrl || ""}
            width={96}
            height={96}
            className="max-h-24 max-w-24 flex-none rounded border border-gray-200 bg-gray-200 object-cover object-center"
            surpressWarning
          />
          <div className="flex min-w-0 flex-1 flex-col">
            <div className="space-y-1 text-sm">
              <h3 className="text-[16px] font-semibold wrap-break-word">
                {displayTitle}
              </h3>
              {isFree && (
                <Badge
                  color="green"
                  className="rounded px-2 font-semibold text-green-600"
                  size="sm"
                >
                  {t("free_gift")}
                </Badge>
              )}
            </div>

            {variantOptions.length > 0 && !isEnrollment && (
              <div className="mt-1 flex flex-wrap gap-1.5">
                {variantOptions.map((o) => (
                  <button
                    key={o.title}
                    onClick={() => {
                      setShowVariantSelection((prev) => !prev);
                    }}
                    disabled={isFree}
                  >
                    <Badge
                      key={o.title}
                      color="gray"
                      className={cn(
                        "h-auto max-w-full rounded px-2 py-1 break-all text-gray-900 capitalize",
                        {
                          "hover:bg-gray-300": !isFree,
                        },
                      )}
                      size="sm"
                    >
                      {o.value}
                    </Badge>
                  </button>
                ))}
              </div>
            )}

            {allowSubscription && !isEnrollment && (
              <div className="flex items-center space-x-2 pt-2 pb-1">
                <input
                  type="checkbox"
                  id={`subscription-${itemId}`}
                  className={`h-4 w-4 rounded border-gray-300 text-black focus:ring-0 ${
                    isQuantityPending || isSubscriptionPending
                      ? "cursor-not-allowed opacity-50"
                      : "cursor-pointer"
                  }`}
                  checked={isSubscribed}
                  disabled={isQuantityPending || isSubscriptionPending}
                  onChange={(e) =>
                    updateSubscription(
                      selectedSubscriptionPlanId,
                      e.target.checked,
                    )
                  }
                />
                <label
                  htmlFor={`subscription-${itemId}`}
                  className={`text-xs ${
                    isQuantityPending || isSubscriptionPending
                      ? "cursor-not-allowed text-gray-400"
                      : "cursor-pointer text-gray-700"
                  }`}
                >
                  {Number(subscribeAndSave) > 0
                    ? t("subscribe_and_save")
                    : t("subscribe")}
                </label>
              </div>
            )}
            {isEnrollment && (
              <div className="mt-1">
                <Badge
                  color="gray"
                  className="rounded px-2 text-gray-900"
                  size="sm"
                >
                  {t("enrollment_pack")}
                </Badge>
              </div>
            )}

            {/* Enrollment Product Details */}
            {isEnrollment && matchingEnrollmentPack && (
              <EnrollmentItem
                productTitle={productTitle}
                variantOptions={variantOptions}
              />
            )}

            {/* Bundle Items Display */}
            {isBundle && bundleItemsToDisplay.length > 0 && (
              <BundleItem
                bundleItems={bundleItemsToDisplay}
                currencySymbol={currencySymbol}
              />
            )}

            {isSubscribed && subscriptionPlans && (
              <div className="pt-1">
                {subscriptionPlans.length === 1 ? (
                  <div className="flex items-center space-x-2">
                    <FluidImage
                      src={`${FLUID_CDN_URL}/images/checkout/icons/arrows-repeat.svg`}
                      alt="repeat"
                      width={12}
                      height={12}
                      className="inline-block"
                    />
                    <div className="text-xs text-gray-900">
                      {subscriptionDetails}
                    </div>
                  </div>
                ) : (
                  <SubscriptionPlansDropdown
                    subscriptionPlans={subscriptionPlans}
                    selectedSubscriptionPlanId={selectedSubscriptionPlanId}
                    onPlanSelect={(planId) => updateSubscription(planId, true)}
                    disabled={isQuantityPending || isSubscriptionPending}
                  />
                )}
              </div>
            )}

            <div className="mt-1 space-y-1 text-sm">
              {isSubscriptionPending ? (
                <div className="flex items-center space-x-2">
                  <div className="h-4 w-16 animate-pulse rounded bg-gray-200" />
                </div>
              ) : /* Backend contract (SubscriptionPlanBlueprinter :cart_item
                     view in fluid-commerce/fluid): subscribeAndSave (raw
                     numeric) and savingsLabel are co-populated — both gate
                     on `cart_item.allow_subscription?` and the same
                     positive-savings condition. Whenever savingsLabel is
                     non-null, subscribeAndSave is also > 0, so this
                     legacy-numeric gate safely covers the savings_label
                     chip render below. If that backend invariant ever
                     changes (e.g. percent-mode stops populating
                     subscribe_and_save), the chip would silently disappear;
                     re-check this gate before relaxing the legacy field
                     on the server side. */
              isSubscribed &&
                subscribeAndSave &&
                Number(subscribeAndSave) > 0 ? (
                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  <span className="text-sm font-medium whitespace-nowrap text-gray-400 line-through">
                    {originalLineTotal}
                  </span>
                  <span className="text-sm font-semibold whitespace-nowrap text-gray-900">
                    {currentLineTotal}
                  </span>
                  <span className="text-xs font-medium whitespace-nowrap text-green-600">
                    {/* Prefer server-formatted savings_label ("Save 10%" / */}
                    {/* "Save $5.00") which already includes the "Save" prefix */}
                    {/* and honors the plan's savings_display_mode. Falls back */}
                    {/* to the legacy quantity-aware currency math when the */}
                    {/* backend hasn't shipped savings_label yet. */}
                    {/* Per-unit vs line-total: savings_label is per unit */}
                    {/* (server-formatted "Save $5.00"), not multiplied by */}
                    {/* quantity. The legacy fallback path below multiplies */}
                    {/* by quantity. Per-unit is intentional and consistent */}
                    {/* with the shop product tile and mobile shop/cart — */}
                    {/* and percent-mode can't be multiplied by quantity at */}
                    {/* all. Line totals above are backend-authoritative when */}
                    {/* the cart response exposes those boundary fields. */}
                    {savingsLabel ? (
                      `(${savingsLabel})`
                    ) : (
                      <>
                        ({t("save")}{" "}
                        {formatCurrency(
                          calculateTotalSavings(subscribeAndSave, quantity),
                          currencyFormatOptions,
                        )}
                        )
                      </>
                    )}
                  </span>
                  {rewardPointsEnabled && (creditablePoints ?? 0) > 0 && (
                    <>
                      <span className="text-lg font-bold text-gray-300">·</span>
                      <span className="text-xs whitespace-nowrap text-gray-500">
                        {t("earn_points", undefined, {
                          points: creditablePoints ?? 0,
                          label: pointsLabel(
                            creditablePoints ?? 0,
                            pointsLabelConfig,
                          ),
                        })}
                      </span>
                    </>
                  )}
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <span
                    className={`text-sm font-semibold ${
                      isFree ? "line-through" : ""
                    }`}
                  >
                    {isFree ? originalLineTotal : currentLineTotal}
                  </span>
                  {isFree && (
                    <span className="text-sm font-semibold">
                      {currentLineTotal}
                    </span>
                  )}
                  {rewardPointsEnabled &&
                    !isFree &&
                    (creditablePoints ?? 0) > 0 && (
                      <>
                        <span className="text-lg font-bold text-gray-300">
                          ·
                        </span>
                        <span className="text-xs whitespace-nowrap text-gray-500">
                          {t("earn_points", undefined, {
                            points: creditablePoints ?? 0,
                            label: pointsLabel(
                              creditablePoints ?? 0,
                              pointsLabelConfig,
                            ),
                          })}
                        </span>
                      </>
                    )}
                </div>
              )}
            </div>

            {/* Per-line QV/CV — rep carts only (non-reps never see volume). */}
            {/* Each metric mirrors the cart-total gating: shown only when > 0. */}
            {displayVolume && (
              <div className="mt-1 flex flex-wrap gap-x-3 text-xs font-medium text-gray-500">
                {cvTotal > 0 && (
                  <span>
                    {t("cv")} {cvTotal}
                  </span>
                )}
                {qvTotal > 0 && (
                  <span>
                    {t("qv")} {qvTotal}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {!isFree && (
          <div className="ml-4 shrink-0">
            <ItemCountButton
              quantity={quantity}
              variantId={variantId}
              cartToken={cartToken}
              deleteOnly={isEnrollment}
              itemId={itemId}
              paymentMethodsIncludes={paymentMethodsIncludes}
              isSubscriptionPending={isSubscriptionPending}
            />
          </div>
        )}
      </div>
      {showVariantSelection && !isFree && (
        <VariantSelectionDropdown
          productId={productId}
          cartToken={cartToken}
          paymentMethodsIncludes={paymentMethodsIncludes ?? null}
          onClose={() => setShowVariantSelection(false)}
          itemId={itemId}
          quantity={quantity}
        />
      )}

      {errors && <CartItemError errors={errors} />}
    </li>
  );
};

export default Product;
